import { Card } from 'react-scale/components/scalers/tasks/ThreadedInteraction/Card';
import Select from 'react-scale/corp/components/UI/Select';
import { Button, Switch, CodeBlock, Spinner, Tooltip, Typography } from '@scale/scaleui';
import { ToolFieldAnnotation } from '@scale/shared/types/response/textcollection';
import JSONInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/JSONInputBuilder';
import { useChatStoreExtract, useSystemPrompt } from '../Chat/hooks/ChatStore.hooks';
import {
  ToolField,
  ToolsAnnotation,
  ChatTaskToolDefinition,
} from '@scale/shared/types/params/textcollection';
import { useState, useEffect, useMemo } from 'react';
import apiFetch from 'react-scale/lib/api-fetch';
import { useGetChatTaskToolsSchemas } from 'react-scale/hooks/chatTaskTools/useGetChatTaskToolSchemas';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Form from '@rjsf/material-ui';
import { pollChatToolStatusToCompletion } from 'react-scale/hooks/chatTaskTools/chatTaskToolStatusHelpers';
import { isLongRunningJobOutput, RESET_OUTPUT_TOOL_ID } from '@scale/shared/chatTaskTools/types';
import _ from 'lodash';
import { CodeBlockCopyButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/shared/CodeBlockCopyButton';

type ToolSectionProps = {
  field: ToolField;
  setAnnotation: (annotation: ToolFieldAnnotation) => void;
  annotation: ToolFieldAnnotation;
};

// When field ids to extract for future use (i.e. copy and pasting tool output) is present,
// we use these magic strings to encapsulate the 2 special cases
// 1. '__str_output__' - when tool output is a string and we want to pull this in for future use
// 2. '__input__' - when we want to pull in tool input for future use (e.g. any params that were passed to the tool)
const MAGIC_INPUT_FIELD_EXTRACTION = '__input__';
const STR_OUTPUT_MAGIC_ID = '__str_output__';

export function ToolSection(props: ToolSectionProps) {
  const { field, setAnnotation, annotation } = props;
  const toolIds: string[] = props.field.tools.map(tool => tool.id);
  const { data } = useGetChatTaskToolsSchemas({ toolIds });
  const assignmentId = useChatStoreExtract(store => store.assignmentId);

  const toolOptions =
    toolIds.map(toolId => ({
      label: data?.toolDefinitions[toolId].name,
      value: toolId,
    })) || [];

  const [selectedTool, setSelectedTool] = useState<{ label: string; value: string }>();
  const type = annotation.type;

  useEffect(() => {
    if (toolOptions.length === 1) {
      setSelectedTool(toolOptions[0] as { label: string; value: string });
    }
  }, [JSON.stringify(toolOptions)]);

  const toolsAndFormData: ToolsAnnotation[] = annotation.response;
  const setToolsAndFormData = (toolsAndFormData: ToolsAnnotation[]) => {
    const prefillFieldIds: Record<string, any> = {};
    // for text collection steps, prefill tool outputs as needed
    if (field.extractFieldIdsForPrefill) {
      for (const [toolOutputFieldId, chatTaskFieldId] of Object.entries(
        field.extractFieldIdsForPrefill,
      )) {
        for (const toolData of toolsAndFormData) {
          try {
            const toolOutput = JSON.parse(toolData.output);
            if (typeof toolOutput === 'string' && toolOutputFieldId === STR_OUTPUT_MAGIC_ID) {
              // Case 1: when tool output is a string and we want to pull this in for future use
              // toolData.output = 'some string', toolOutputFieldId = '__str_output__'
              // extractFieldIdsForPrefill: { '__str_output__': 'chat_task_id' }
              prefillFieldIds[chatTaskFieldId] = toolOutput;
            } else if (toolOutput && _.get(toolOutput, toolOutputFieldId)) {
              // Case 2: fetch from tool output and copy into chat task field, with . notation to access objects
              // toolData.output = { a: { b: 'some val' }}, toolOutputFieldId = 'a.b'
              // extractFieldIdsForPrefill: { 'a.b.': 'chat_task_id' }
              prefillFieldIds[chatTaskFieldId as string] = _.get(toolOutput, toolOutputFieldId);
            } else if (toolOutputFieldId.startsWith(MAGIC_INPUT_FIELD_EXTRACTION)) {
              // Case 3: fetch from tool input and copy into chat task field, with . notation to access objects
              // toolData.input = { someInput: 'some val' }, toolOutputFieldId = '__input__.someInput'
              // extractFieldIdsForPrefill: { '__input__.someInput': 'chat_task_id' }
              const toolInputFieldId = toolOutputFieldId.replace(
                MAGIC_INPUT_FIELD_EXTRACTION + '.',
                '',
              );
              if (toolData.input && toolData.input[toolInputFieldId]) {
                prefillFieldIds[chatTaskFieldId as string] = _.get(
                  toolData.input,
                  toolInputFieldId,
                );
              }
            }
          } catch (_err) {
            // ignore errors
          }
        }
      }
    }
    setAnnotation({
      type,
      field_id: field.field_id,
      response: toolsAndFormData,
      prefillFieldIds,
    });
  };

  const showAddTool = useMemo(() => {
    return (
      !props.field.enableSingleToolOnly ||
      (props.field.enableSingleToolOnly && toolsAndFormData.length === 0)
    );
  }, [props.field.enableSingleToolOnly, toolsAndFormData.length]);

  return (
    <>
      <Card
        title={'Tools'}
        subtitle={'Select tools below to help verify that the model response is correct.'}
      >
        {toolsAndFormData.map((t, i) => (
          <Tool
            key={i}
            assignmentId={assignmentId}
            toolDefinition={t.toolDefinition}
            inputData={t.input}
            setInputData={input => {
              toolsAndFormData[i].input = input;
              setToolsAndFormData([...toolsAndFormData]);
            }}
            outputData={t.output}
            setOutputData={output => {
              toolsAndFormData[i].output = output;
              setToolsAndFormData([...toolsAndFormData]);
            }}
            deleteTool={() => {
              toolsAndFormData.splice(i, 1);
              setToolsAndFormData([...toolsAndFormData]);
            }}
            resetAndBlockOutput={() => {
              // Setting this to null will block validation of this step
              toolsAndFormData[i].output = null;
              setToolsAndFormData([...toolsAndFormData]);
            }}
            field={field}
          />
        ))}
        <br />
        <div className="flex items-center">
          <Select
            onChange={async (option: { label: string; value: string }) => {
              setSelectedTool(option);
            }}
            className="min-w-[200px]"
            clearable={false}
            value={selectedTool}
            options={toolOptions}
          />
          {showAddTool && (
            <Button
              type="submit"
              className="custom-submit-button h-9 ml-1 p-3"
              onClick={() => {
                if (selectedTool && data?.toolDefinitions[selectedTool.value]) {
                  const toolDefinitions = data.toolDefinitions;
                  setToolsAndFormData([
                    ...toolsAndFormData,
                    {
                      ...toolDefinitions[selectedTool.value],
                      toolDefinition: toolDefinitions[selectedTool.value],
                      input: {},
                      output: undefined,
                    },
                  ]);
                }
              }}
            >
              +
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}

function Tool(props: {
  toolDefinition: ChatTaskToolDefinition;
  inputData: any;
  setInputData: (value: any) => void;
  outputData: any;
  setOutputData: (value: any) => void;
  deleteTool: () => void;
  resetAndBlockOutput: () => void;
  field: ToolField;
  assignmentId?: string;
}) {
  const systemPromptMessage = useSystemPrompt();
  const {
    inputData: inputFormData,
    setInputData,
    toolDefinition,
    setOutputData,
    outputData,
    deleteTool,
    resetAndBlockOutput: resetOutput,
    field,
    assignmentId,
  } = props;

  const { id, name, description, inputSchema, sendPreviousInput, sendAssignmentId } =
    toolDefinition;
  const [showJson, setJson] = useState(false);
  const [loadingTool, setLoadingTool] = useState(false);
  const [error, setError] = useState<{ severity: string; message: string } | null>(null);
  const [previousInput, setPreviousInput] = useState<any>(inputFormData ?? {});

  const shouldDisableRunTool =
    inputFormData &&
    typeof inputFormData === 'object' &&
    inputSchema.type === 'object' &&
    inputSchema.properties &&
    Object.keys(inputSchema.properties).length > 0 &&
    (Object.keys(inputFormData).length === 0 ||
      _.compact(Object.values(inputFormData)).length === 0);

  const maybeOutputDataObject = useMemo(() => {
    try {
      if (typeof outputData === 'string') {
        return JSON.parse(outputData);
      }
      return outputData;
    } catch (e) {
      return outputData;
    }
  }, [outputData]);

  return (
    <>
      <Switch className="mb-3" label="Show JSON" checked={showJson} onCheckedChange={setJson} />
      <Card
        title={
          <div className="inline-flex cursor-pointer">
            {name} <DeleteForeverIcon onClick={deleteTool} />
          </div>
        }
        subtitle={description}
      >
        {showJson && (
          <JSONInputBuilder
            name={'Parameters'}
            existingValue={inputFormData || {}}
            updateProperty={(value: any) => {
              setInputData(value);
            }}
            required={false}
          />
        )}
        {!showJson && (
          <Form
            uiSchema={{
              ...toolDefinition.inputUISchema,
              'ui:submitButtonOptions': { norender: true },
            }}
            formData={inputFormData}
            onChange={({ formData }: { formData: any }) => setInputData(formData)}
            schema={inputSchema}
          />
        )}
        <Card
          title={
            <>
              Output
              <Tooltip
                text="Please make sure you've filled out the input fields before running the tool."
                disabled={!shouldDisableRunTool}
                target={
                  <Button
                    className="custom-submit-button ml-2"
                    onClick={async () => {
                      if (
                        field.extractFieldIdsForPrefill &&
                        Object.keys(previousInput).length > 0
                      ) {
                        const chatTaskFieldIds = Object.values(field.extractFieldIdsForPrefill);
                        if (
                          !window.confirm(
                            `Running this tool may overwrite the ${chatTaskFieldIds.join(
                              ', ',
                            )} fields. Are you sure?`,
                          )
                        ) {
                          return;
                        }
                      }
                      try {
                        setError(null);
                        setLoadingTool(true);
                        let output: any = await apiFetch(null, `internal/genai/invokeChatTool`, {
                          method: 'POST',
                          body: {
                            toolId: id,
                            params: {
                              ...inputFormData,
                              ...(sendPreviousInput ? { previous: previousInput } : {}),
                              ...(sendAssignmentId ? { assignmentId } : {}),
                            },
                            taskMetadata: {
                              ...(systemPromptMessage && {
                                systemPrompt: systemPromptMessage.content,
                              }),
                            },
                          },
                        });
                        if (isLongRunningJobOutput(output)) {
                          const pollResult = await pollChatToolStatusToCompletion(output.jobId, id);
                          output = pollResult.result;
                        }
                        setPreviousInput(inputFormData);
                        setOutputData(JSON.stringify(output, null, 2));
                      } catch (e) {
                        if (e && e.response && e.response.error?.includes(RESET_OUTPUT_TOOL_ID)) {
                          resetOutput();
                          setPreviousInput({});
                          setError({
                            message: `${e.response.error.split(RESET_OUTPUT_TOOL_ID)[1]}`,
                            severity: 'info',
                          });
                        } else {
                          setError({
                            message:
                              'The last run failed. The result below may not reflect the latest run.',
                            severity: 'error',
                          });
                        }
                      } finally {
                        setLoadingTool(false);
                      }
                    }}
                    disabled={loadingTool || shouldDisableRunTool}
                  >
                    {loadingTool ? <Spinner size="sm" /> : 'Run Tool'}
                  </Button>
                }
              />
            </>
          }
          subtitle={'The output of the tool from above. Please check that the output is correct.'}
        >
          <p style={{ color: error?.severity === 'error' ? 'red' : 'black' }}>
            {error?.message ?? ''}
          </p>
          <div style={{ background: 'rgb(40, 41, 54)' }}>
            {/* @ts-ignore */}
            {loadingTool && <Spinner size="sm" />}
            {!loadingTool && (
              <>
                {/* Custom feature for Audio RLHF to allow easy viewing + copying of audio/video URLs */}
                <div className="flex flex-col gap-2">
                  {maybeOutputDataObject?.audioUrl && (
                    <MediaLink url={maybeOutputDataObject.audioUrl} type="audio" />
                  )}
                  {maybeOutputDataObject?.videoUrl && (
                    <MediaLink url={maybeOutputDataObject.videoUrl} type="video" />
                  )}
                </div>
                <CodeBlock
                  languageMatch={'json' as unknown as RegExpExecArray}
                  code={outputData || '{}'}
                  enableSoftWrap={true}
                />
              </>
            )}
          </div>
        </Card>
      </Card>
      <br />
    </>
  );
}

const MediaLink = ({ url, type }: { url: string; type: 'audio' | 'video' }) => (
  <div className="flex items-center gap-2">
    <Typography.Paragraph1>
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-400">
        Click here to open {type}
      </a>
    </Typography.Paragraph1>
    <CodeBlockCopyButton className="bg-primary-400" code={url} />
  </div>
);
